import { z } from 'zod';
/**
 * Motifs de refus proposés au client. `OTHER_OFFER_ACCEPTED` n'y figure pas : ce
 * motif est réservé au refus induit posé par le système quand une autre
 * proposition de la même demande est confirmée. Un client ne peut pas le choisir,
 * et le schéma le refuse en entrée.
 */
export const CLIENT_DECLINE_REASONS = ['TOO_EXPENSIVE', 'DATES_NOT_SUITABLE', 'OTHER'];
/**
 * Libellés des motifs de refus — une seule source pour l'espace client, la page
 * publique et le back-office. `OTHER_OFFER_ACCEPTED` y est présent pour la
 * relecture back-office uniquement.
 */
export const ESTIMATE_DECLINE_REASON_LABELS = {
    DATES_NOT_SUITABLE: 'Les dates ne me conviennent pas',
    OTHER: 'Autre',
    OTHER_OFFER_ACCEPTED: 'Déclinée automatiquement (autre proposition confirmée)',
    TOO_EXPENSIVE: 'Trop cher',
};
/**
 * Le libellé d'un motif de refus, ou le code brut quand il n'en a pas.
 *
 * Les refus d'avant l'unification portent des codes libres : mieux vaut afficher
 * le code que rien, et le lot de libellés reste fermé pour qui l'indexe par clé.
 */
export function estimateDeclineReasonLabel(reason) {
    if (!reason)
        return '';
    return reason in ESTIMATE_DECLINE_REASON_LABELS
        ? ESTIMATE_DECLINE_REASON_LABELS[reason]
        : reason;
}
/**
 * Longueur minimale d'une demande de modification. « ok » ou « changer » ne dit
 * rien à l'agent qui la lira ; dix caractères forcent une phrase.
 */
export const MODIFICATION_REQUEST_MIN_LENGTH = 10;
export const declineEstimateResponseSchema = z
    .object({
    comment: z.string().trim().max(2000).optional(),
    reason: z.enum(CLIENT_DECLINE_REASONS),
})
    .refine((value) => value.reason !== 'OTHER' || !!value.comment, {
    message: 'Merci de préciser votre motif.',
    path: ['comment'],
});
/**
 * Réponse ÉCRITE d'un agent à une demande de modification.
 *
 * Même plancher que la demande du client, et pour la même raison : « ok » ne dit
 * rien à qui le lira — ici le client, dans sa boîte mail.
 */
export const replyToModificationRequestSchema = z.object({
    message: z
        .string()
        .trim()
        .min(MODIFICATION_REQUEST_MIN_LENGTH, {
        message: `Votre réponse doit faire au moins ${MODIFICATION_REQUEST_MIN_LENGTH} caractères`,
    })
        .max(2000, { message: 'Votre réponse ne peut pas dépasser 2000 caractères' }),
});
export const requestEstimateModificationSchema = z.object({
    message: z
        .string()
        .trim()
        .min(MODIFICATION_REQUEST_MIN_LENGTH, 'Décrivez en quelques mots ce que vous souhaitez modifier.')
        .max(2000),
});
/**
 * Ce qu'on présente au mur d'accès aux données personnelles : le secret du
 * message, ou le nom de famille. Exactement l'un des deux — envoyer les deux
 * laisserait croire qu'un nom peut « compléter » un secret invalide.
 *
 * Le même schéma sert les trois murs (proposition, demande, réservation) : la
 * règle est la même, et la dupliquer par surface finirait par la faire diverger.
 */
export const personalDataAccessSchema = z
    .object({
    lastName: z.string().trim().min(1).max(200).optional(),
    token: z.string().trim().min(1).max(200).optional(),
})
    .refine((value) => !!value.token !== !!value.lastName, {
    message: 'Indiquez le nom de famille du client ou d’un voyageur.',
    path: ['lastName'],
});
